import React, { useRef, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { Sparkles, Upload, X, Loader2, CheckCircle2 } from "lucide-react";

/**
 * AI Extract panel — drops into any product editor. Admin picks up to 3
 * product photos (front label · composition · dosage); calls Claude vision
 * via POST /admin/bulk-products/extract; on success, the consumer's
 * `onExtracted` callback receives the structured JSON and is expected to
 * patch the form (name/category/composition/dosage/indications).
 *
 * On a successful extract we ALSO upload every picked photo to /api/uploads
 * and pass the resulting URLs back as `_imageUrls`, so the consumer can
 * append them to the product's gallery automatically — avoids the common
 * "product saved with no thumbnail" pitfall.
 *
 * Reuses the exact same backend endpoint as `/admin/bulk-products` so the
 * prompt + model stay in lock-step.
 */
export default function AIProductExtractPanel({ onExtracted, defaultName = "", testId = "ai-extract" }) {
    const [productImage, setProductImage] = useState(null);
    const [compositionImage, setCompositionImage] = useState(null);
    const [dosageImage, setDosageImage] = useState(null);
    const [extracting, setExtracting] = useState(false);
    const [lastResult, setLastResult] = useState(null);
    const productRef = useRef(null);
    const compRef = useRef(null);
    const doseRef = useRef(null);

    const reset = () => {
        setProductImage(null);
        setCompositionImage(null);
        setDosageImage(null);
        setLastResult(null);
        if (productRef.current) productRef.current.value = "";
        if (compRef.current) compRef.current.value = "";
        if (doseRef.current) doseRef.current.value = "";
    };

    const validatePick = (file) => {
        if (!file) return null;
        if (file.size > 6 * 1024 * 1024) {
            toast.error(`${file.name} is over 6 MB — please pick a smaller image.`);
            return null;
        }
        if (!/^image\/(jpeg|jpg|png|webp)$/.test(file.type)) {
            toast.error(`${file.name}: only JPEG / PNG / WEBP supported`);
            return null;
        }
        return file;
    };

    const extract = async () => {
        if (!productImage) { toast.error("Pick the product photo first."); return; }
        setExtracting(true);
        setLastResult(null);
        const fd = new FormData();
        fd.append("product_image", productImage);
        if (compositionImage) fd.append("composition_image", compositionImage);
        if (dosageImage) fd.append("dosage_image", dosageImage);
        if (defaultName) fd.append("fallback_name", defaultName);
        try {
            const { data } = await api.post("/admin/bulk-products/extract", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 90000,
            });

            // Also upload the picked photos to the catalog gallery so the
            // product saves WITH a thumbnail. We do this in parallel with the
            // Claude response since extract already succeeded — failures here
            // are non-fatal (admin can still upload manually).
            const uploads = [productImage, compositionImage, dosageImage].filter(Boolean);
            const urls = [];
            for (const f of uploads) {
                try {
                    const upFd = new FormData();
                    upFd.append("file", f);
                    // eslint-disable-next-line no-await-in-loop
                    const up = await api.post("/uploads", upFd, { headers: { "Content-Type": "multipart/form-data" } });
                    if (up.data?.url) urls.push(up.data.url);
                } catch (uploadErr) {
                    // Don't fail the whole extract just because one image upload hiccupped.
                    console.warn("AI panel image upload failed:", uploadErr);
                }
            }

            const enriched = { ...data, _imageUrls: urls };
            setLastResult(enriched);
            onExtracted?.(enriched);
            toast.success(
                urls.length > 0
                    ? `AI extracted: ${data.name || "(no name)"} · ${urls.length} photo${urls.length === 1 ? "" : "s"} added to gallery`
                    : `AI extracted: ${data.name || "(no name)"}`
            );
        } catch (e) {
            const msg = e.response?.data?.detail || e.message || "Extraction failed";
            toast.error(msg);
        } finally {
            setExtracting(false);
        }
    };

    return (
        <div className="border-2 border-dashed border-sky-300 bg-sky-50/40 p-4" data-testid={testId}>
            <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-4 h-4 text-sky-700" />
                <span className="text-xs uppercase tracking-widest text-sky-700 font-medium">AI extract — same model as Bulk add</span>
            </div>
            <p className="text-xs text-stone-600 mb-3">
                Drop a photo of the product front, and (optionally) the composition and dosage labels. Claude vision reads the labels and pre-fills <strong>name</strong>, <strong>category</strong>, <strong>composition</strong>, <strong>dosage</strong> and <strong>indications</strong>. You can edit any field before saving.
            </p>

            <div className="grid grid-cols-3 gap-2 mb-3">
                <Slot label="Product front *" value={productImage} onChange={(f) => setProductImage(validatePick(f))} inputRef={productRef} testId={`${testId}-product`} />
                <Slot label="Composition label" value={compositionImage} onChange={(f) => setCompositionImage(validatePick(f))} inputRef={compRef} testId={`${testId}-composition`} />
                <Slot label="Dosage label" value={dosageImage} onChange={(f) => setDosageImage(validatePick(f))} inputRef={doseRef} testId={`${testId}-dosage`} />
            </div>

            <div className="flex flex-wrap gap-2 items-center justify-between">
                <div className="text-[11px] text-stone-500">
                    {productImage ? "Ready — click Extract." : "Pick the product photo to enable Extract."}
                </div>
                <div className="flex gap-2">
                    {(productImage || compositionImage || dosageImage || lastResult) && (
                        <Button variant="outline" onClick={reset} disabled={extracting} className="rounded-none border-stone-300" data-testid={`${testId}-reset`}>
                            <X className="w-4 h-4 mr-1" />Clear
                        </Button>
                    )}
                    <Button
                        onClick={extract}
                        disabled={!productImage || extracting}
                        className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50"
                        data-testid={`${testId}-go`}
                    >
                        {extracting
                            ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Reading labels…</>
                            : <><Sparkles className="w-4 h-4 mr-1" />Extract &amp; fill</>}
                    </Button>
                </div>
            </div>

            {lastResult && (
                <div className="mt-3 text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 p-2 flex items-start gap-2" data-testid={`${testId}-result`}>
                    <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <div className="flex-1">
                        Filled <strong>{lastResult.name || "(unnamed)"}</strong> · AI confidence: <strong>{lastResult.confidence || "?"}</strong>
                        {Array.isArray(lastResult._imageUrls) && lastResult._imageUrls.length > 0 && (
                            <> · <strong>{lastResult._imageUrls.length} photo{lastResult._imageUrls.length === 1 ? "" : "s"} added to gallery</strong></>
                        )}
                        {lastResult.notes && <div className="mt-0.5 text-stone-600">{lastResult.notes}</div>}
                        <div className="mt-1 text-stone-500">Review every field below — AI can mis-read small print.</div>
                    </div>
                </div>
            )}
        </div>
    );
}

const Slot = ({ label, value, onChange, inputRef, testId }) => (
    <div>
        <Label className="text-[10px] uppercase tracking-widest text-stone-500 block mb-1">{label}</Label>
        <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className={`block w-full aspect-square border ${value ? "border-sky-400" : "border-dashed border-stone-300"} bg-white overflow-hidden hover:border-sky-500 transition-colors`}
            data-testid={`${testId}-btn`}
        >
            {value ? (
                <img src={URL.createObjectURL(value)} alt={value.name} className="w-full h-full object-cover" />
            ) : (
                <div className="w-full h-full grid place-items-center text-stone-400">
                    <Upload className="w-5 h-5" />
                </div>
            )}
        </button>
        <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => onChange(e.target.files?.[0] || null)}
            data-testid={`${testId}-input`}
        />
    </div>
);
